import { GlobalStyles, ThemeProvider as MuiThemeProvider } from '@mui/material';
import React, { useCallback, useLayoutEffect, useMemo } from 'react';

import { getTheme } from 'constants/theme';
import { useWindowDimensions } from 'hooks/common';
import { useAppDispatch, useAppSelector } from 'hooks/store';
import { changeThemeMode, ThemeModeSelector } from 'store/common';

interface IProps {
  children: React.ReactNode;
}

const ThemeProvider: React.FC<IProps> = ({ children }) => {
  const themeMode = useAppSelector(ThemeModeSelector);

  const theme = useMemo(() => getTheme(themeMode), [themeMode]);
  const dispatch = useAppDispatch();
  const { width } = useWindowDimensions();

  const getThemeOnLoad = useCallback(() => {
    const themeFromLocalStorage = localStorage.getItem('theme');
    const userPreferredMode = window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';

    // On First Load Check User Preferred Mode
    if (!themeFromLocalStorage) {
      dispatch(changeThemeMode(userPreferredMode));
      return;
    }

    // On Second Load get Data from localStorage
    if (themeFromLocalStorage === 'light' || themeFromLocalStorage === 'dark') {
      dispatch(changeThemeMode(themeFromLocalStorage));
    }
  }, []);

  useLayoutEffect(() => {
    getThemeOnLoad();

    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      dispatch(
        changeThemeMode(
          window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
        ),
      );
    });
  }, []);

  return (
    <MuiThemeProvider theme={theme}>
      <GlobalStyles
        styles={{
          '*': { fontFamily: "'Nunito', sans-serif", fontSize: width < 450 ? 'small' : 'unset' },
          '.ql-clipboard': { display: 'none !important' },
        }}
      />
      {children}
    </MuiThemeProvider>
  );
};

export default ThemeProvider;
